import { useState } from 'react';
import { 
  FaClipboardList, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaFileAlt,
  FaCheck,
  FaExclamationTriangle
} from 'react-icons/fa';
import styles from './ClaimsBook.module.css';

const ClaimsBook = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    documentType: 'dni',
    documentNumber: '',
    claimType: 'complaint',
    description: '',
    acceptTerms: false
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'El nombre es obligatorio';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (!formData.documentNumber.trim()) newErrors.documentNumber = 'El número de documento es obligatorio';
    if (!formData.description.trim()) newErrors.description = 'La descripción es obligatoria';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'Debe aceptar los términos';
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Here you would typically send the data to your backend
    console.log('Form submitted:', formData);
    
    // Show success message
    setSubmitted(true);
    
    // Reset form after submission
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      address: '',
      documentType: 'dni',
      documentNumber: '',
      claimType: 'complaint',
      description: '',
      acceptTerms: false
    });
  };

  return (
    <>
      <section className={styles.claimsHero}>
        <div className={styles.heroContent}>
          <div className={styles.badge}>
            <FaClipboardList className={styles.badgeIcon} />
            <span>Atención al Cliente</span>
          </div>
          <h1>Libro de <span>Reclamaciones</span></h1>
          <p>
            Valoramos tu opinión y estamos comprometidos a brindarte el mejor servicio. 
            Si tienes alguna queja o reclamo, por favor completa el siguiente formulario.
          </p>
        </div>
      </section>

      <section className={styles.claimsSection}>
        <div className={styles.claimsContainer}>
          {submitted ? (
            <div className={styles.successMessage}>
              <div className={styles.successIcon}>
                <FaCheck />
              </div>
              <h2>¡Reclamación Enviada!</h2>
              <p>
                Hemos recibido tu reclamación correctamente. Nuestro equipo de atención al cliente 
                se pondrá en contacto contigo en un plazo máximo de 48 horas hábiles.
              </p>
              <p>
                Tu número de referencia es: <strong>REC-{Math.floor(Math.random() * 10000).toString().padStart(4, '0')}</strong>
              </p>
              <button 
                className={styles.newClaimButton}
                onClick={() => setSubmitted(false)}
              >
                Realizar otra reclamación
              </button>
            </div>
          ) : (
            <>
              <div className={styles.formIntro}>
                <h2>Formulario de Reclamación</h2>
                <p>
                  Por favor, completa todos los campos obligatorios marcados con un asterisco (*).
                  Tu información será tratada de manera confidencial según nuestra política de privacidad.
                </p>
              </div>

              <form className={styles.claimsForm} onSubmit={handleSubmit}>
                <div className={styles.formSection}>
                  <h3>Información Personal</h3>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="fullName">
                      Nombre Completo *
                      <FaUser className={styles.inputIcon} />
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={errors.fullName ? styles.inputError : ''}
                    />
                    {errors.fullName && <span className={styles.errorText}>{errors.fullName}</span>}
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="email">
                        Correo Electrónico *
                        <FaEnvelope className={styles.inputIcon} />
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? styles.inputError : ''}
                      />
                      {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="phone">
                        Teléfono *
                        <FaPhone className={styles.inputIcon} />
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={errors.phone ? styles.inputError : ''}
                      />
                      {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="address">
                      Dirección
                      <FaMapMarkerAlt className={styles.inputIcon} />
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="documentType">Tipo de Documento *</label>
                      <select
                        id="documentType"
                        name="documentType"
                        value={formData.documentType}
                        onChange={handleChange}
                      >
                        <option value="dni">DNI</option>
                        <option value="passport">Pasaporte</option>
                        <option value="residenceCard">Tarjeta de Residencia</option>
                        <option value="other">Otro</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="documentNumber">
                        Número de Documento *
                        <FaFileAlt className={styles.inputIcon} />
                      </label>
                      <input
                        type="text"
                        id="documentNumber"
                        name="documentNumber"
                        value={formData.documentNumber}
                        onChange={handleChange}
                        className={errors.documentNumber ? styles.inputError : ''}
                      />
                      {errors.documentNumber && <span className={styles.errorText}>{errors.documentNumber}</span>}
                    </div>
                  </div>
                </div>

                <div className={styles.formSection}>
                  <h3>Detalles de la Reclamación</h3>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="claimType">Tipo de Reclamación *</label>
                    <div className={styles.radioGroup}>
                      <div className={styles.radioOption}>
                        <input
                          type="radio"
                          id="complaint"
                          name="claimType"
                          value="complaint"
                          checked={formData.claimType === 'complaint'}
                          onChange={handleChange}
                        />
                        <label htmlFor="complaint">Queja</label>
                      </div>
                      <div className={styles.radioOption}>
                        <input
                          type="radio"
                          id="claim"
                          name="claimType"
                          value="claim"
                          checked={formData.claimType === 'claim'}
                          onChange={handleChange}
                        />
                        <label htmlFor="claim">Reclamo</label>
                      </div>
                      <div className={styles.radioOption}>
                        <input
                          type="radio"
                          id="suggestion"
                          name="claimType"
                          value="suggestion"
                          checked={formData.claimType === 'suggestion'}
                          onChange={handleChange}
                        />
                        <label htmlFor="suggestion">Sugerencia</label>
                      </div>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="description">
                      Descripción detallada *
                      <span className={styles.charCount}>
                        {formData.description.length}/500
                      </span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      maxLength="500"
                      placeholder="Describe tu queja, reclamo o sugerencia con el mayor detalle posible..."
                      className={errors.description ? styles.inputError : ''}
                    ></textarea>
                    {errors.description && <span className={styles.errorText}>{errors.description}</span>}
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div className={styles.termsGroup}>
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                    />
                    <label htmlFor="acceptTerms">
                      Acepto la <a href="/terminos" target="_blank">Política de Privacidad</a> y el tratamiento de mis datos personales *
                    </label>
                    {errors.acceptTerms && <span className={styles.errorText}>{errors.acceptTerms}</span>}
                  </div>

                  <div className={styles.formActions}>
                    <button type="submit" className={styles.submitButton}>
                      Enviar Reclamación
                    </button>
                  </div>
                </div>

                <div className={styles.formDisclaimer}>
                  <FaExclamationTriangle />
                  <p>
                    De acuerdo con la normativa vigente, nos comprometemos a responder a tu reclamación 
                    en un plazo máximo de 30 días hábiles. Todos los datos proporcionados serán tratados 
                    conforme a nuestra política de privacidad.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default ClaimsBook;